import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api, SESSION_EXPIRED_EVENT } from "../../lib/api";
import type { User } from "../../lib/types";
import { AuthContext, type AuthContextValue } from "./auth-context";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<User>("/auth/me").then(setUser).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const clearExpiredSession = () => {
      sessionStorage.removeItem("voyage_csrf");
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, clearExpiredSession);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, clearExpiredSession);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login: async (credentials) => {
      const result = await api<{ user: User; csrf_token: string }>("/auth/login", { method: "POST", body: JSON.stringify(credentials) });
      sessionStorage.setItem("voyage_csrf", result.csrf_token);
      queryClient.clear();
      setUser(result.user);
    },
    register: async (input) => {
      const result = await api<{ user: User; csrf_token: string }>("/auth/register", { method: "POST", body: JSON.stringify(input) });
      sessionStorage.setItem("voyage_csrf", result.csrf_token);
      queryClient.clear();
      setUser(result.user);
    },
    logout: async () => {
      try {
        await api("/auth/logout", { method: "POST" });
      } finally {
        sessionStorage.removeItem("voyage_csrf");
        queryClient.clear();
        setUser(null);
      }
    },
  }), [loading, queryClient, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
